import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import ThumbnailHoverModal from '../ThumbnailHoverModal';
import spotlightService from '../../services/SpotlightService';
import { cacheService } from '../../services/CacheService';
import { optimizeImageUrl, getResponsiveImagePreset } from '../../utils/ImageUtils';
import { RelatedContentSkeleton } from './SkeletonLoaders';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const RelatedContent = ({ items = [], onSelect, loading = false }) => {
  const navigate = useNavigate();
  const [imageErrors, setImageErrors] = useState({});

  // State for hover modal
  const [showHoverModal, setShowHoverModal] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [hoveredItem, setHoveredItem] = useState(null);
  const hoverTimeoutRef = useRef(null);

  // Show skeleton while loading
  if (loading) {
    return <RelatedContentSkeleton />;
  }

  if (!items || items.length === 0) {
    return null;
  }

  const handleItemClick = item => {
    if (onSelect) {
      onSelect(item._id || item.slug, item.type || 'unknown');
    } else {
      // Default navigation if no onSelect handler is provided
      const slug = item.slug || item._id;
      const contentType = item.programming_type === 'Series' ? 'series' : 'channel';
      navigate(`/${contentType}/${slug}`);
    }
  };

  const handleImageError = itemId => {
    setImageErrors(prev => ({ ...prev, [itemId]: true }));
  };

  // Calculate modal position (improved positioning like ChannelCard)
  const calculateModalPosition = element => {
    if (!element) return { x: 0, y: 0 };

    const rect = element.getBoundingClientRect();
    const modalWidth = 320;
    const modalHeight = 320;
    const padding = 16;

    // Calculate horizontal position (center the modal over the thumbnail)
    let x = rect.left + rect.width / 2 - modalWidth / 2;

    // Adjust if modal would go off-screen horizontally
    if (x < padding) {
      x = padding;
    } else if (x + modalWidth > window.innerWidth - padding) {
      x = window.innerWidth - modalWidth - padding;
    }

    // Calculate vertical position (try above first, then below)
    let y = rect.top - modalHeight - 8;

    // If not enough space above, position below
    if (y < padding) {
      y = rect.bottom + 8;
    }

    // Add scroll offset
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    return {
      x: x + scrollLeft,
      y: y + scrollTop,
    };
  };

  // Handle hover modal show (500ms delay like homepage)
  const handleHoverModalShow = useCallback((item, element) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Start prefetching directly without using hook
    const itemSlug = item.slug || item._id;
    if (itemSlug) {
      // Prefetch logic inline
      setTimeout(async () => {
        try {
          // Check if already cached to avoid redundant requests
          const cacheKey = `channel_${itemSlug}`;
          if (cacheService.get(cacheKey)) {
            return;
          }

          await spotlightService.getChannelBySlug(itemSlug).catch(e => {
            throw new Error(e);
          });
        } catch (error) {
          console.warn(`Prefetch failed for channel ${itemSlug}:`, error);
        }
      }, 250);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      if (element) {
        const position = calculateModalPosition(element);
        setModalPosition(position);
        setHoveredItem(item);
        setShowHoverModal(true);
      }
    }, 500); // 500ms delay before showing modal (same as homepage)
  }, []);

  // Handle hover modal hide with delay
  const handleHoverModalHide = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Add delay before hiding to allow user to reach the modal
    hoverTimeoutRef.current = setTimeout(() => {
      setShowHoverModal(false);
      setHoveredItem(null);
    }, 300); // 300ms delay before hiding
  }, []);

  // Handle modal mouse enter (keep modal open)
  const handleModalMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  // Handle modal mouse leave (hide modal immediately)
  const handleModalMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowHoverModal(false);
    setHoveredItem(null);
  }, []);

  // Limit to 10 items
  const displayItems = items.slice(0, 10);

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  return (
    <div className="related-content mb-8">
      <h2 className="text-xl font-bold text-white mb-4">You May Also Like</h2>
      <Slider {...sliderSettings}>
        {displayItems.map((item, index) => {
          const itemId = item._id || index;
          // Get optimized thumbnail using ImageUtils
          const rawThumbnailUrl = item.spotlight_poster || item.poster;
          const thumbnail = rawThumbnailUrl
            ? optimizeImageUrl(rawThumbnailUrl, getResponsiveImagePreset('RELATED_CONTENT'))
            : null;

          const title = item.title || 'Untitled';
          const contentType = item.programming_type || (item.is_series ? 'Series' : 'Movie');

          return (
            <div key={itemId} className="px-2">
              <div
                className="cursor-pointer transition-transform hover:scale-105 related-item"
                onClick={() => handleItemClick(item)}
                onMouseEnter={e => handleHoverModalShow(item, e.currentTarget)}
                onMouseLeave={handleHoverModalHide}
              >
                <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-800 mb-2">
                  {thumbnail && !imageErrors[itemId] ? (
                    <img
                      src={thumbnail}
                      alt={title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={() => handleImageError(itemId)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-12 w-12 text-white/30"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                        />
                      </svg>
                    </div>
                  )}

                  {/* Homepage-style hover overlay with play button */}
                  <div className="absolute inset-0 flex items-end transition-opacity duration-300 opacity-0 bg-gradient-to-t from-black/80 to-transparent hover:opacity-100">
                    <div className="w-full p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-white truncate block">{title}</span>
                          <span className="text-white/70 text-xs">{contentType}</span>
                        </div>
                        <button
                          className="flex items-center justify-center w-8 h-8 p-1 text-white rounded-full bg-[#56bdaf] hover:bg-[#4aa89c] ml-2 flex-shrink-0 transition-colors"
                          onClick={e => {
                            e.stopPropagation();
                            handleItemClick(item);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>

      {/* Hover Modal */}
      {/* {hoveredItem && (
        <ThumbnailHoverModal
          channel={hoveredItem}
          isVisible={showHoverModal}
          position={modalPosition}
          onMouseEnter={handleModalMouseEnter}
          onMouseLeave={handleModalMouseLeave}
        />
      )} */}
    </div>
  );
};

export default RelatedContent;
